import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAddCategoryItem } from '../basket/potentials'
import { CategoryPicker } from '../components/CategoryPicker'
import { useTranslation } from '../localization/useTranslation'
import { type CategoryOption, useCategoryOptions } from '../search/categories'
import { useAddFlowStore } from '../store/addFlow'
import { useAuthStore } from '../store/auth'
import { useSetPageTitle } from '../store/pageTitle'
import { useToast } from '../ui/toast'
import { useMoneyFmt } from '../util/useMoney'

/**
 * The add-by-category page (route `/category`). Reached from the finder — either the "can't find it?"
 * fallback tile in the photo results, or, for categories-only shops, straight after taking a photo.
 * A real route (not a finder sub-view) so it scrolls to the top on entry and gets its own history
 * entry. The photo taken on the finder is carried here via the add-flow store and attached to the
 * created potential (Core prices the item from the category's flat value).
 */
export function AddCategoryPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const fmt = useMoneyFmt()
  const showToast = useToast((s) => s.show)
  const photoUrl = useAddFlowStore((s) => s.photoUrl)
  const setPhotoUrl = useAddFlowStore((s) => s.setPhotoUrl)
  const { add: addCategory, adding } = useAddCategoryItem()
  const { hasCategories } = useCategoryOptions()
  const productSearchEnabled = useAuthStore((s) => s.config.productSearchEnabled)

  useSetPageTitle(t('photo.categoryPickTitle'), '')

  // Categories-only shops have no matches to return to, so Back means "retake the photo"; the photo
  // fallback returns to its results. We go back to whichever finder we came from (`/add` for a feed
  // customer, `/items` for a no-feed customer) via history, not a hard-coded path.
  const categoryOnly = !productSearchEnabled && hasCategories

  // Distinguishes an intentional exit (below) from the orphan guard, so clearing the photo just
  // before navigating doesn't make the guard fire a second, wrong navigation.
  const leaving = useRef(false)
  const addPending = useRef(false)
  const mounted = useRef(false)
  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])

  // Guard: landing here without a saved photo has nothing to work with
  // — bounce back to the finder rather than show an orphaned picker.
  useEffect(() => {
    if (!photoUrl && !leaving.current) navigate('/items', { replace: true })
  }, [photoUrl, navigate])

  // Return to the finder we came from. `clearPhoto` drops the photo (add / replace / category-only
  // back → the finder starts fresh); keeping it lets the photo-search fallback restore its results.
  const leaveToFinder = (clearPhoto: boolean) => {
    leaving.current = true
    if (clearPhoto) setPhotoUrl(null)
    navigate(-1)
  }

  const onPick = async (c: CategoryOption) => {
    if (addPending.current) return
    addPending.current = true
    const photo = photoUrl
    const identity = useAddFlowStore.getState().photoIdent
    try {
      await addCategory(c.id, photo)
      const current = useAddFlowStore.getState()
      if (current.photoIdent !== identity || current.photoUrl !== photo) return
      if (mounted.current) leaveToFinder(true)
      else setPhotoUrl(null)
      showToast(t('finder.added', { name: c.name, amount: fmt(c.value) }))
    } catch {
      showToast(t('photo.uploadFailed'))
    } finally {
      addPending.current = false
    }
  }

  const onBack = () => leaveToFinder(categoryOnly)
  const onReplace = () => leaveToFinder(true)

  return (
    <div className="step">
      <fieldset disabled={adding} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
        <CategoryPicker photoUrl={photoUrl} onBack={onBack} onReplace={onReplace} onPick={onPick} />
      </fieldset>
    </div>
  )
}
